import React from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faStar,
  faCar,
  faHotel,
  faUtensils,
  faPlane,
  faInfoCircle,
  faShareNodes,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { formatCurrency } from '../../utils/currencyFormatter';
import { calculatePackageTotals } from './pricingUtils';

// Mappings Configuration
// Confidence mapping: high->green, medium->blue, low->gray
const CONFIDENCE_STYLES = {
  high: "bg-green-100 text-green-700 border-green-200",
  medium: "bg-blue-100 text-blue-700 border-blue-200",
  low: "bg-gray-100 text-gray-500 border-gray-200",
};

// Source mapping: ai->Live, cache->Cached, master->Verified, fallback->Estimate
const SOURCE_MAPPING = {
  ai: { label: "Live", styles: "bg-green-50 text-green-600 border-green-100" },
  cache: { label: "Cached", styles: "bg-gray-50 text-gray-500 border-gray-200" },
  master: { label: "Verified", styles: "bg-blue-50 text-blue-600 border-blue-100" },
  fallback: { label: "Estimate", styles: "bg-yellow-50 text-yellow-600 border-yellow-200" },
};

// Reusable Components
export const ConfidenceBadge = ({ confidence }) => {
  return null;
};

export const SourceBadge = ({ source }) => {
  return null;
};

export const PackageCard = ({ pkg, totalTravelers, estimateData, currency, isBestValue, isMostPopular, tripData }) => {

  // Calculate dynamic info from tripData
  const destinationsArray = tripData?.destinations && tripData.destinations.length > 0 
    ? tripData.destinations 
    : (tripData?.destination ? [tripData.destination] : []);
  const numCities = destinationsArray.length || 1;
  const numRooms = tripData?.rooms?.length || 1;
  const startDate = tripData?.startDate ? new Date(tripData.startDate) : null;
  const endDate = tripData?.endDate ? new Date(tripData.endDate) : null;
  let nights = 1;
  if (startDate && endDate) {
    const diffTime = Math.abs(endDate - startDate);
    nights = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  if (nights === 0) nights = 1;

  const bgGradient = isMostPopular
    ? "bg-gradient-to-br from-blue-50 to-blue-100/50"
    : "bg-white";

  const handleSelectPackage = () => {
    toast.success(`${pkg.name} Selected! An agent will contact you shortly to proceed with booking.`);
  };

  // Quick wrapper to automatically apply the trip's currency
  const formatCost = (amount) => formatCurrency(amount, currency);

  const formatMealPlanText = (name) => {
    if (!name) return "Breakfast";
    const upper = name.toUpperCase();
    if (upper.includes("MAP")) return "Breakfast + Dinner";
    if (upper.includes("AP")) return "All Meals";
    if (upper.includes("EP")) return "Room Only";
    return "Breakfast";
  };
  const mealPlanText = formatMealPlanText(pkg.name);

  // Generate a subtitle based on package name
  let subtitle = "Standard Hotels + Breakfast";
  if (pkg?.name?.includes("3 Star")) subtitle = "Silver Standard Hotels + Breakfast";
  if (pkg?.name?.includes("4 Star")) subtitle = "Gold Standard Hotels + Breakfast";
  if (pkg?.name?.includes("5 Star")) subtitle = "Premium Luxury Hotels + Breakfast";

  const { 
    includeTransport, transportAmount, 
    grandTotalMid, grandTotalMin, grandTotalMax,
    perPersonDoubleSharingMid, perPersonDoubleSharingMin, perPersonDoubleSharingMax
  } = calculatePackageTotals(pkg, estimateData);

  // Derive avg if we have min/max for the display
  const avgGrand = grandTotalMid ? formatCost(grandTotalMid) : `${formatCost(grandTotalMin)} - ${formatCost(grandTotalMax)}`;
  const avgPerPerson = perPersonDoubleSharingMid ? formatCost(perPersonDoubleSharingMid) : `${formatCost(perPersonDoubleSharingMin)} - ${formatCost(perPersonDoubleSharingMax)}`;
  const packageTotal = grandTotalMid || grandTotalMin || 0;

  return (
    <div className={`bg-white rounded-xl shadow-sm mb-4 overflow-hidden ${isBestValue ? 'border-[1.5px] border-[#0F826B]' : isMostPopular ? 'border-[1.5px] border-[#2563EB]' : 'border border-gray-200'}`}>

      {/* Header Section */}
      <div className="p-4 border-b border-gray-100 relative">
        {isBestValue && (
          <div className="absolute top-0 right-0 bg-[#0F826B] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg uppercase tracking-wider">
            Best Value
          </div>
        )}
        {isMostPopular && (
          <div className="absolute top-0 left-0 right-0 bg-[#2563EB] text-white text-[10px] font-bold px-3 py-1.5 flex justify-between items-center uppercase tracking-wider">
            <span>Most Popular</span>
            <div className="flex gap-0.5">
              <FontAwesomeIcon icon={faStar} className="w-2.5 h-2.5" />
              <FontAwesomeIcon icon={faStar} className="w-2.5 h-2.5" />
              <FontAwesomeIcon icon={faStar} className="w-2.5 h-2.5" />
              <FontAwesomeIcon icon={faStar} className="w-2.5 h-2.5" />
            </div>
          </div>
        )}

        <div className={`flex items-center gap-1 mb-1 ${isMostPopular ? 'mt-6' : ''} text-[#0F826B]`}>
          <FontAwesomeIcon icon={faStar} className="text-[10px]" />
          <FontAwesomeIcon icon={faStar} className="text-[10px]" />
          <FontAwesomeIcon icon={faStar} className="text-[10px]" />
          <span className="text-[10px] font-bold uppercase tracking-wider ml-1 text-[#0F826B]">{mealPlanText}</span>
        </div>
        {/* Render Badges Together */}
        <div className="flex items-center gap-2 mb-2">
          <SourceBadge source={pkg?.source} />
          <ConfidenceBadge confidence={pkg?.confidence} />
        </div>
        <h3 className="text-[15px] font-bold text-gray-900 mb-0">
          {pkg?.name?.replace(' Hotels', ' Standard Hotels').replace(/\bMAP\b/i, 'Breakfast + Dinner').replace(/\bCP\b/i, 'Breakfast').replace(/\bEP\b/i, 'Room Only').replace(/\bAP\b/i, 'All Meals')}
        </h3>
        <p className="text-[10px] text-gray-500 mt-0.5">{nights} nights • {numCities} cities • {numRooms} rooms</p>
      </div>

      {/* Pricing Row */}
      <div className="flex gap-2 sm:gap-4 p-4 border-b border-gray-100">
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Grand Total</span>
          <span className="text-[16px] sm:text-xl font-black text-gray-900 leading-none whitespace-nowrap">
            {avgGrand}
          </span>
          <span className="text-[10px] text-gray-400 mt-1 truncate">for full group ({totalTravelers} guests)</span>
        </div>
        <div className="flex flex-col pl-1 sm:pl-2 justify-center flex-1 min-w-0">
          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Per Person</span>
          <span className="text-[14px] sm:text-lg font-bold text-teal-600 leading-none whitespace-nowrap">
            {pkg.perPersonDoubleSharing?.min ? `${formatCost(pkg.perPersonDoubleSharing.min)} - ${formatCost(pkg.perPersonDoubleSharing.max)}` : formatCost(pkg.perPersonDoubleSharing?.mid || 0)}
          </span>
          <span className="text-[10px] text-gray-400 mt-0.5 truncate">Double Sharing</span>
        </div>
      </div>

      {/* Breakdown */}
      <div className="p-4">
        <div className="space-y-3">
          <div className="flex justify-between items-center text-[11px]">
            <div className="flex items-center gap-2 text-gray-500">
              <FontAwesomeIcon icon={faHotel} className="w-3" />
              <span>Hotel Accommodation</span>
            </div>
            <span className="font-bold text-gray-800">{formatCost(pkg?.breakdown?.hotelMeal)}</span>
          </div>

          <div className="flex justify-between items-center text-[11px]">
            <div className="flex items-center gap-2 text-gray-500">
              <FontAwesomeIcon icon={faUtensils} className="w-3" />
              <span>Meal Plan ({mealPlanText.replace('Breakfast', 'BF').replace('Dinner', 'DIN')})</span>
            </div>
            <span className="font-bold text-[#0F826B]">Included</span>
          </div>

          <div className="flex justify-between items-center text-[11px]">
            <div className="flex items-center gap-2 text-gray-500">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
              <span>Activities (Shared)</span>
            </div>
            <span className="font-bold text-gray-800">{formatCost(pkg?.breakdown?.activities)}</span>
          </div>

          <div className="flex justify-between items-center text-[11px]">
            <div className="flex items-center gap-2 text-gray-500">
              <FontAwesomeIcon icon={faCar} className="w-3" />
              <span>Transfers (Airport + Local)</span>
            </div>
            <span className="font-bold text-gray-800">{formatCost(pkg?.breakdown?.transfers)}</span>
          </div>
          
          {includeTransport && (
            <div className="flex justify-between items-center text-[11px]">
              <div className="flex items-center gap-2 text-gray-500">
                <FontAwesomeIcon icon={faPlane} className="w-3" />
                <span>Transport (Flights / Trains)</span>
              </div>
              <span className="font-bold text-gray-800">{formatCost(transportAmount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Package Total */}
      <div className="flex justify-between items-center px-4 pt-3 pb-4 border-t border-gray-100">
        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-900">Package Total</span>
        <span className="text-lg font-black text-gray-900">
          {formatCost(packageTotal)}
        </span>
      </div>

    </div>
  );
};

export default PackageCard;
